import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import IyoAuth from '../../../auth/Auth';
import Handlers from './handlers';
import DialogHelpers from './data-stream-edit-dialog/helpers';

const initializeColumns = (initialColumns, queryClient, setState) => {
  return initialColumns.concat(
    {
      field: 'provider_schema',
      headerName: 'Provider Schema',
      description: 'Provider Schema',
      minWidth: 150,
      renderCell: (params) => {
        return (
          <>
            <Tooltip title="View Schema">
              <IconButton
                color="primary"
                onClick={(event) =>
                  Handlers.handleDialogOpen(
                    event,
                    'provider_schema',
                    params.row,
                    setState
                  )
                }
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
            &nbsp;
            <Tooltip title="View Properties">
              <IconButton
                color="primary"
                onClick={(event) =>
                  Handlers.handleDialogOpen(
                    event,
                    'provider_properties',
                    params.row,
                    setState
                  )
                }
              >
                <BuildCircleIcon />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: 'data_stream_provider_properties',
      headerName: 'Datastream Properties',
      description: 'Datastream Properties',
      minWidth: 150,
      renderCell: (params) => {
        return (
          <>
            <Tooltip title="View Properties">
              <IconButton
                color="primary"
                onClick={(event) =>
                  Handlers.handleDialogOpen(
                    event,
                    'data_stream_provider_properties',
                    params.row,
                    setState
                  )
                }
              >
                <BuildCircleIcon />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      description: 'Actions',
      minWidth: 145,
      renderCell: (params) => {
        return (
          <>
            <Tooltip title="Edit">
              <IconButton
                color="primary"
                onClick={(event) =>
                  Handlers.handleEdit(event, params.row, queryClient, setState)
                }
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    }
  );
};

const produceError = (error, setState) => {
  const message =
    error?.response?.data === undefined
      ? error.toString()
      : `${error.message}: ${JSON.stringify(error.response.data)}`;

  setState.setErrorOpen(false);
  setState.setErrDisplayText(message);
  setState.setErrorOpen(true);

  console.error(message);
};

const updateDataStream = async (values, state, putDataStream, schema) => {
  let body = {
    provider_properties: {},
  };

  for (let field in values) {
    if (values[field] !== state.rowEditing[field]) {
      const value = mapValueToSchema(field, values[field], schema);
      if (state.dataStreamFields.includes(field)) {
        body[field] = value;
      } else {
        body.provider_properties[field] = value;
      }
    }
  }

  body['provider_id'] = state.providerId;
  body['owner_id'] = state.ownerId;
  body['cron'] = state.cron;
  body['last_retrieval_date'] = state.lastRetrievalDate;
  // Only a PC Agent stream has a device; the dialog clears the selection for every other
  // provider. Sent as null rather than omitted so moving a stream off a PC Agent provider
  // actually releases the device - an omitted key is dropped by the JSON round trip and
  // would leave the old one attached.
  body['device_id'] = state.deviceId ?? null;
  body['updated_by'] = await IyoAuth.getUser();
  body['id'] = state.rowEditing.id;
  // Read from the form, not from state: the editor writes through Formik's setFieldValue,
  // so there is no state.partition_configs to read.
  //
  // The seeded example varies by provider scheme, so compare against all of them rather
  // than one constant, or an untouched example gets saved as real configuration. An empty
  // value is sent as null so clearing every rule actually clears the column - undefined
  // would be dropped by the PUT's JSON round trip and leave the old rules in place.
  const partitionConfigs = values.partition_configs;
  if (partitionConfigs === undefined || DialogHelpers.isExampleValue(partitionConfigs)) {
    delete body['partition_configs'];
  } else {
    body['partition_configs'] = partitionConfigs === '' ? null : partitionConfigs;
  }

  // Awaited, so the caller resumes only once the PUT has landed and the mutation's
  // onSuccess has invalidated getDataStreams. mutate() returns before either, which left
  // the grid refetching the pre-save rows.
  await putDataStream.mutateAsync(body);
};

const createDataStream = async (
  values,
  state,
  setState,
  dataStreamFields,
  postDataStream,
  schema
) => {
  let body = {
    provider_properties: {},
  };

  for (let [key, value] of Object.entries(values)) {
    value = mapValueToSchema(key, value, schema);
    if (dataStreamFields.includes(key)) {
      body[key] = value;
    } else {
      body.provider_properties[key] = value;
    }
  }

  body['provider_id'] = state.providerId;
  body['owner_id'] = state.ownerId;
  body['cron'] = state.cron;
  body['last_retrieval_date'] = state.lastRetrievalDate;
  body['device_id'] = state.deviceId ?? null;

  // As in updateDataStream: an untouched example is not configuration, and saving it would
  // route rows to the placeholder owner in the example.
  if (!body['partition_configs'] || DialogHelpers.isExampleValue(body['partition_configs'])) {
    delete body['partition_configs'];
  }

  await postDataStream.mutateAsync({
    ...body,
    created_by: await IyoAuth.getUser(),
    updated_by: await IyoAuth.getUser(),
  });
};

function mapValueToSchema(field, value, schema) {
  if (!value) {
    return undefined;
  }

  if (!schema) {
    return value;
  }

  const schemaInfo = schema.find(function (item) {
    return item.name === field;
  });
  switch (schemaInfo?.type.toLowerCase()) {
    case 'integer':
      value = Number.parseInt(value);
      break;

    case 'float':
      value = Number.parseFloat(value);
      break;

    case 'boolean':
      let boolVal = value.toString().trim().toLowerCase();
      switch (boolVal) {
        case 'true':
        case '1':
          value = true;
          break;
        case 'false':
        case '0':
          value = false;
          break;
        default:
          value = undefined;
          break;
      }
      break;

    // ms-ngest validates provider_properties against the provider's JSON schema, so a
    // non-array here is a 400. NO comma splitting: site names contain commas, so a stored
    // string is taken whole rather than guessed at.
    case 'array': {
      const clean = (items) => items.map((item) => String(item).trim()).filter(Boolean);

      if (Array.isArray(value)) {
        value = clean(value);
        break;
      }

      const text = String(value).trim();
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          value = clean(parsed);
          break;
        }
      } catch (err) {
        // Not JSON; treat as a single value.
      }
      value = clean([text]);
      break;
    }

    case 'object':
      value = Object.create(value);
      break;
  }
  return value;
}

export default {
  initializeColumns,
  produceError,
  createDataStream,
  updateDataStream,
};
